import { z } from 'zod';
import { ErrorCode } from '../../../../shared/error-codes';
import { ObjectIdSchema } from '../../../../shared/object-id.schema';
/**
 * Enum values mirror the labels HubSpot stores (the n8n flow passes them
 * straight through to the contact properties), so they stay German.
 */ export const PartnerLeadSalutationSchema = z.enum([
    'Herr',
    'Frau',
    'Divers'
]);
export const PartnerLeadInterestProofSchema = z.enum([
    'Vorheriger persönlicher Kontakt (Telefon oder Meeting)',
    'Lead hat aktiv Informationen angefragt (Produkt oder Angebot)',
    'Follow-up nach Event oder Webinar'
]);
export const PartnerLeadReactionSchema = z.enum([
    'Ja',
    'Nein'
]);
/**
 * The Calendly URIs the `event_scheduled` widget payload emits. The backend
 * verifies the booking against the Calendly API, so only genuinely
 * Calendly-shaped resource URIs are accepted — never arbitrary strings.
 */ export const CALENDLY_SCHEDULED_EVENT_URI_PATTERN = /^https:\/\/api\.calendly\.com\/scheduled_events\/[A-Za-z0-9-]+$/;
export const CALENDLY_INVITEE_URI_PATTERN = /^https:\/\/api\.calendly\.com\/scheduled_events\/[A-Za-z0-9-]+\/invitees\/[A-Za-z0-9-]+$/;
// The form state holds '' before a booking exists, so allow the empty string
// at the type level; the superRefine below enforces presence on submit.
const calendlyUri = (pattern)=>z.union([
        z.literal(''),
        z.string().regex(pattern, 'partner.newLead.appointmentRequired')
    ]).optional();
export const PartnerLeadLicenseTypeSchema = z.enum([
    'B2B-Lizenz (Für Endkunden)',
    'Partner-Lizenz (Mit Seats)',
    'Partner-Lizenz (Ohne Seats)'
]);
/**
 * Create Partner Lead Request
 * POST /api/v2/partner/leads
 *
 * Clean camelCase fields; the backend maps them onto the German webhook keys
 * the existing n8n flow expects and injects the partner context server-side.
 * `message`s are i18n keys — the form renders them via `t(...)`.
 *
 * Conditional rules (mirroring the Framer form):
 *  - a lead may only be created when the contact reacted positively
 *  - the licence type is required once the lead is positively qualified
 */ export const CreatePartnerLeadRequestSchema = z.object({
    // Selection fields are optional at the type level (so the form can start
    // with empty selects + placeholders) and made required via superRefine.
    salutation: PartnerLeadSalutationSchema.optional(),
    title: z.string().optional(),
    firstName: z.string().min(1, ErrorCode.FIELD_REQUIRED),
    lastName: z.string().min(1, ErrorCode.FIELD_REQUIRED),
    email: z.email(ErrorCode.USER_INVALID_EMAIL),
    phone: z.string().optional(),
    company: z.string().min(1, ErrorCode.FIELD_REQUIRED),
    role: z.string().min(1, ErrorCode.FIELD_REQUIRED),
    interestProof: PartnerLeadInterestProofSchema.optional(),
    /** YYYY-MM-DD */ contactDate: z.string().min(1, ErrorCode.FIELD_REQUIRED),
    positiveReaction: PartnerLeadReactionSchema.optional(),
    licenseType: PartnerLeadLicenseTypeSchema.optional(),
    typicalIndustries: z.string().optional(),
    openQuestions: z.string().optional(),
    comment: z.string().optional(),
    // A fixed Calendly appointment is mandatory: a lead is only created once one
    // is booked (replaces the old `wantsDemo` checkbox). The two URIs come from
    // the Calendly `event_scheduled` payload; the backend verifies the booking
    // against the Calendly API before forwarding the lead.
    appointmentEventUri: calendlyUri(CALENDLY_SCHEDULED_EVENT_URI_PATTERN),
    appointmentInviteeUri: calendlyUri(CALENDLY_INVITEE_URI_PATTERN),
    /** The rep's own call-list contact the lead came from; it gets marked as submitted. */ contactId: ObjectIdSchema.optional()
}).superRefine((data, ctx)=>{
    const required = (field)=>{
        if (!data[field]) {
            ctx.addIssue({
                code: 'custom',
                path: [
                    field
                ],
                message: ErrorCode.FIELD_REQUIRED
            });
            return false;
        }
        return true;
    };
    required('salutation');
    required('interestProof');
    if (!required('positiveReaction')) return;
    // Mirrors the Framer gate: only positively-qualified leads may be created.
    if (data.positiveReaction !== 'Ja') {
        ctx.addIssue({
            code: 'custom',
            path: [
                'positiveReaction'
            ],
            message: 'partner.newLead.notQualifiedNotice'
        });
        return;
    }
    if (!data.licenseType) {
        ctx.addIssue({
            code: 'custom',
            path: [
                'licenseType'
            ],
            message: ErrorCode.FIELD_REQUIRED
        });
    }
    // A booked Calendly appointment is required — no lead without a fixed
    // meeting. Both URIs are needed so the backend can verify the booking, and
    // the invitee must belong to the submitted event.
    if (!data.appointmentEventUri || !data.appointmentInviteeUri) {
        ctx.addIssue({
            code: 'custom',
            path: [
                'appointmentEventUri'
            ],
            message: 'partner.newLead.appointmentRequired'
        });
        return;
    }
    if (!data.appointmentInviteeUri.startsWith(`${data.appointmentEventUri}/invitees/`)) {
        ctx.addIssue({
            code: 'custom',
            path: [
                'appointmentInviteeUri'
            ],
            message: 'partner.newLead.appointmentRequired'
        });
    }
});
export const CreatePartnerLeadResponseSchema = z.object({
    success: z.boolean()
});

//# sourceMappingURL=create-partner-lead.schema.js.map